/**
 * ItemDiffView — the exact bytes one work item's package member deploys (R4 item-body response).
 *
 * THE SAME DIFF UX AS THE CHANGES BOARD. R4 hands back `body`/`baseline` text per file, not a
 * server diff, so this used to render its own static line diff. It now lists the item's files the
 * way `ChangesetPane` lists a component's, and opens one in `FileDiff` — the repo's ONE Monaco diff
 * surface — with a synthesised `{file, diff}` handed in as `preloaded`: the same header, status
 * chip, inline/split toggle, expand overlay and Back, read-only (R4 has no hunk endpoint). List →
 * detail → back, exactly what the reader learned on the changes board.
 *
 * NODE-RENDERABLE IN LIST MODE. `FileDiff` is `React.lazy` (Monaco needs a DOM), so the list is
 * what the static test suite renders; the detail is exercised in the browser harness.
 */
import { Suspense, lazy, useMemo, useState } from "react";
import { AlertTriangle, ChevronLeft, ChevronRight, FileText, Loader2, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { FILE_STATUS } from "@/services/multi-agent/changesetFiles";
import type { FileWithDiff } from "@/services/multi-agent/changesets";
import { monacoLanguage } from "@/services/multi-agent/codePreview";
import type { ItemBody } from "@/services/multi-agent/laneV2/types";
import type { File as ChangesetFile, FileStatus } from "@/services/multi-agent/protocol";
import { diffLines, normalizeForStatus } from "./views";

const FileDiff = lazy(() => import("../changeset/FileDiff").then((m) => ({ default: m.FileDiff })));

/** Where this diff sits among the task's changes: the breadcrumb root and the prev/next walk. */
export interface DiffNav {
  /** The "Changes" crumb and the ONE back level: returns to the task's change list. */
  onChanges: () => void;
  /** 1-based position among the changed components; absent when there is only this one. */
  position?: { index: number; total: number };
  onPrev?: () => void;
  onNext?: () => void;
}

export interface ItemDiffViewProps {
  body: ItemBody;
  nav?: DiffNav;
  /** Land straight on this file's diff (Back returns to the list). Ignored if it is not in `body.files`. */
  initialPath?: string | null;
  className?: string;
}

type ItemFile = ItemBody["files"][number];

/** A Monaco language from the file's extension — the fence-label map, keyed by what sfdx names things. */
export function itemFileLanguage(path: string): string {
  const ext = path.slice(path.lastIndexOf(".") + 1).toLowerCase();
  const label =
    ext === "cls" || ext === "trigger"
      ? "apex"
      : ext === "xml"
        ? "xml"
        : ext === "js"
          ? "javascript"
          : ext === "html" || ext === "cmp" || ext === "app"
            ? "html"
            : ext === "css"
              ? "css"
              : ext === "json"
                ? "json"
                : "text";
  return monacoLanguage(label);
}

/** What the changes board would call this file: created, modified, or (op) deleted. */
export function itemFileStatus(op: string, file: Pick<ItemFile, "baseline"> & Partial<ItemFile>, truncated = false): FileStatus {
  if (op === "delete") return "deleted";
  if (op === "create" || file.baseline === null) return "created";
  const wire = wireUnchanged(file);
  if (wire !== null) return wire ? "unchanged" : "modified";
  // Fallback until the wire says: same text modulo line endings / trailing whitespace. Never on
  // truncated texts: two cut-off prefixes can be equal while the files differ beyond the cut.
  if (!truncated && file.body != null && normalizeForStatus(file.body) === normalizeForStatus(file.baseline)) return "unchanged";
  return "modified";
}

/**
 * The backend's own verdict, when it sends one (`unchanged` on the R4 file, i.e. package member
 * `body_changed === false`). It takes precedence over the local comparison. Returns null when absent.
 * The field is the generated contract type (`ItemFileUnchanged`).
 */
function wireUnchanged(file: Partial<Pick<ItemFile, "unchanged">>): boolean | null {
  return typeof file.unchanged === "boolean" ? file.unchanged : null;
}

/** Hint for an Unchanged file whose raw texts still differ (wire verdict or fallback alike). */
export const END_ONLY_HINT = "line endings / whitespace at end only";

/** A `-meta.xml` file is a sidecar of the component's source file. */
export function isSidecarPath(path: string): boolean {
  return path.endsWith("-meta.xml");
}

/**
 * `FileDiff`'s inputs from R4's two strings. `file_id` is the path (unique within one item), the
 * stats come from the same line diff the old static view drew, and there are no hunks: R4 offers
 * nothing to accept.
 */
export function preloadedDiffFor(body: ItemBody, file: ItemFile): FileWithDiff {
  const status = itemFileStatus(body.op, file, body.truncated);
  const language = itemFileLanguage(file.path);
  const before = status === "created" ? null : file.baseline;
  const after = file.body;
  const lines = diffLines(before ?? "", after ?? "");
  const changesetFile: ChangesetFile = {
    file_id: file.path,
    component_id: body.work_item_id,
    path: file.path,
    role: "source",
    language,
    status,
    before,
    after,
    size_bytes: new TextEncoder().encode(after ?? before ?? "").length,
  };
  return {
    file: changesetFile,
    diff: {
      diff_id: `r4:${body.work_item_id}:${file.path}`,
      file_id: file.path,
      component_id: body.work_item_id,
      path: file.path,
      language,
      before,
      after,
      hunks: [],
      stats: {
        added: lines.filter((l) => l.type === "add").length,
        removed: lines.filter((l) => l.type === "remove").length,
      },
    },
  };
}

function TruncationNote({ className }: { className?: string }) {
  return (
    <p className={cn("mx-3.5 mb-2 flex items-center gap-1.5 rounded border border-ma-warn-line px-2.5 py-1.5 text-[11px] text-ma-warn-text", className)}>
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
      One or more files were too large to show in full and are cut off below.
    </p>
  );
}

function baseName(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

/** "Changes › Component › file" with prev/next across the changed components. */
function DiffCrumb({ nav, body, filePath, onShowFiles }: { nav: DiffNav; body: ItemBody; filePath: string | null; onShowFiles: () => void }) {
  const link = "rounded px-1 py-0.5 hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3.5 py-2 text-[12px] text-muted-foreground">
      <nav aria-label="Breadcrumb" data-lane-v2-diff-crumb="" className="flex min-w-0 flex-wrap items-center gap-0.5">
        <button type="button" onClick={nav.onChanges} className={link}>
          Changes
        </button>
        <span aria-hidden>›</span>
        {filePath ? (
          <button type="button" onClick={onShowFiles} className={cn(link, "font-mono")}>
            {body.full_name}
          </button>
        ) : (
          <span className="px-1 py-0.5 font-mono text-foreground" aria-current="page">
            {body.full_name}
          </span>
        )}
        {filePath && (
          <>
            <span aria-hidden>›</span>
            <span className="px-1 py-0.5 font-mono text-foreground" aria-current="page" title={filePath}>
              {baseName(filePath)}
            </span>
          </>
        )}
      </nav>
      {nav.position && nav.position.total > 1 && (
        <div className="flex items-center gap-1" data-lane-v2-diff-walk="">
          <button type="button" onClick={nav.onPrev} disabled={!nav.onPrev} aria-label="Previous change" className={cn(link, "disabled:opacity-40")}>
            <ChevronLeft aria-hidden className="h-3.5 w-3.5" />
          </button>
          <span className="tabular-nums" aria-label={`File ${nav.position.index} of ${nav.position.total}`}>
            {nav.position.index} / {nav.position.total}
          </span>
          <button type="button" onClick={nav.onNext} disabled={!nav.onNext} aria-label="Next change" className={cn(link, "disabled:opacity-40")}>
            <ChevronRight aria-hidden className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

export function ItemDiffView({ body, nav, initialPath = null, className }: ItemDiffViewProps) {
  const [openPath, setOpenPath] = useState<string | null>(initialPath);
  const openFile = useMemo(() => body.files.find((f) => f.path === openPath) ?? null, [body.files, openPath]);
  const preloaded = useMemo(() => (openFile ? preloadedDiffFor(body, openFile) : null), [body, openFile]);

  const renderRow = (file: ItemFile) => {
    const fileStatus = itemFileStatus(body.op, file, body.truncated);
    const status = FILE_STATUS[fileStatus];
    const hint = fileStatus === "unchanged" && file.body != null && file.baseline != null && file.body !== file.baseline;
    const empty = file.body === null;
    return (
      <li key={file.path}>
        <button
          type="button"
          onClick={() => setOpenPath(file.path)}
          disabled={empty}
          data-item-file={file.path}
          title={empty ? `${file.path} · no content` : `Review ${file.path}`}
          className="group flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/50 disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent"
        >
          <FileText aria-hidden className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1 truncate font-mono">{file.path}</span>
          {hint && <span className="shrink-0 text-[11px]">{END_ONLY_HINT}</span>}
          {empty ? (
            <span className="shrink-0 text-[11px]">No content.</span>
          ) : (
            <span className={`shrink-0 rounded border px-1 py-px text-[11px] font-semibold ${status.tone}`}>{status.label}</span>
          )}
          {empty ? (
            <span aria-hidden className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ChevronRight aria-hidden className="h-3.5 w-3.5 shrink-0 transition-colors group-hover:text-foreground" />
          )}
        </button>
      </li>
    );
  };
  // Sidecars collapse behind their source file; a sidecar-only item (e.g. custom metadata) stays open.
  const hasSource = body.files.some((f) => !isSidecarPath(f.path));
  const sidecars = hasSource ? body.files.filter((f) => isSidecarPath(f.path)) : [];
  const sidecarsModified = sidecars.filter((f) => itemFileStatus(body.op, f, body.truncated) === "modified").length;
  const primary = hasSource ? body.files.filter((f) => !isSidecarPath(f.path)) : body.files;

  if (preloaded) {
    return (
      <section aria-labelledby="lanev2-item-diff-title" className={cn("rounded-md border border-border bg-card", className)} data-mode="file">
        <h3 id="lanev2-item-diff-title" className="sr-only">
          {body.metadata_type} · {body.full_name}
        </h3>
        {nav && <DiffCrumb nav={nav} body={body} filePath={openPath} onShowFiles={() => setOpenPath(null)} />}
        {body.truncated && <TruncationNote className="mt-2" />}
        <Suspense
          fallback={
            <p role="status" className="flex items-center gap-2 px-3.5 py-4 text-[12px] text-muted-foreground">
              <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" /> Loading diff…
            </p>
          }
        >
          <FileDiff key={preloaded.file.file_id} file={preloaded.file} preloaded={preloaded} onBack={nav ? nav.onChanges : () => setOpenPath(null)} />
        </Suspense>
      </section>
    );
  }

  return (
    <section aria-labelledby="lanev2-item-diff-title" className={cn("rounded-md border border-border bg-card", className)} data-mode="list">
      {nav && <DiffCrumb nav={nav} body={body} filePath={null} onShowFiles={() => setOpenPath(null)} />}
      <header className="px-3.5 py-2.5">
        <h3 id="lanev2-item-diff-title" className="text-[13px] font-semibold text-foreground">
          {body.metadata_type} · {body.full_name}
        </h3>
      </header>

      {body.truncated && <TruncationNote />}

      {!body.baseline_known && body.op !== "delete" && body.files.length > 0 && (
        <p className="mx-3.5 mb-2 text-[11px] text-muted-foreground">
          The org's current version of this component is unknown, so each file is shown as it will be written rather than as a comparison.
        </p>
      )}

      {body.op === "delete" ? (
        <p className="flex items-center gap-1.5 border-t border-border px-3.5 py-3 text-[12px] text-ma-err-text">
          <Trash2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {body.full_name} will be deleted.
        </p>
      ) : body.files.length === 0 ? (
        <p className="border-t border-border px-3.5 py-3 text-[12px] text-muted-foreground">No file changes.</p>
      ) : (
        <ul className="border-t border-border p-1">
          {primary.map(renderRow)}
          {sidecars.length > 0 && (
            <li>
              <details data-item-sidecars>
                <summary className="cursor-pointer px-2 py-1 text-[11px] text-muted-foreground">
                  Supporting files ({sidecars.length}
                  {sidecarsModified > 0 ? `, ${sidecarsModified} modified` : ""})
                </summary>
                <ul>{sidecars.map(renderRow)}</ul>
              </details>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}
